import { CellClickedEvent, GridApi, GridReadyEvent, ICellRendererParams } from '@ag-grid-community/core';
import { Component, HostBinding, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FieldExpression, NodeExpression, term, toString as luceneToString } from 'lucene';
import { fromEvent, Subscription } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { AgGridModule } from '@ag-grid-community/angular';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf } from '@angular/common';

import { pageTransitions } from '../../../../../../../shared/animations/page-transitions';
import { CommonColDef, RadioBtnColDef } from '../../../../../../../shared/datacenter/column-defs';
import { clientSideAgGridModules, CommonGridOptions } from '../../../../../../../shared/datacenter/grid-options';
import { AddSubOrgPageContent } from '../../../../../../../shared/models/add-suborg-info';
import { TranslationService } from '../../../../../../../shared/services/translation.service';
import { OrganizationDetails } from '../../../../../../models/organization';
import { AddSubOrganizationService } from '../../../../../../services/add-suborg.service';
import { LoadingSpinnerComponent } from '../../../../../../../shared/components/utils/loading-spinner.component';

const resizeObservable = fromEvent(globalThis, 'resize');
@Component({
	selector: 'copo-step-matching-organizations',
	templateUrl: './step-matching-organizations.component.html',
	styleUrl: './step-matching-organizations.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		TranslateModule,
		AgGridModule,

		MatFormFieldModule,
		LoadingSpinnerComponent,
	],
})

export class StepMatchingOrganizationsComponent extends AddSubOrgPageContent implements OnDestroy, OnInit {
	@HostBinding('class.passthrough') readonly passthrough = true;
	organizationName = '';
	vatNumber = '';
	organizationId = '';
	sub?: Subscription;
	matchingOrgs: OrganizationDetails[] = [];
	newSubOrg!: OrganizationDetails;
	newSubOrgRowData: OrganizationDetails[] = [];
	isLoading = true;
	isAnyOneSelected = false;
	nextClicked = false;
	nodeSelectedMatchingOrg?: number;
	isNewSubOrg?: boolean;
	gridApi?: GridApi;
	gridNewApi?: GridApi;
	selectedRow?: OrganizationDetails;
	vatNumberExists = false;
	readonly modules = clientSideAgGridModules;
	gridOptions: CommonGridOptions<OrganizationDetails>;

	newSubOrgGridOptions: CommonGridOptions<OrganizationDetails>;

	private resizeSub?: Subscription;

	constructor(
		private readonly addSubOrgService: AddSubOrganizationService,
		private readonly translationService: TranslationService,
	) {
		super();
		this.gridOptions = new CommonGridOptions<OrganizationDetails>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			domLayout: 'autoHeight',
			columnDefs: [
				new RadioBtnColDef<OrganizationDetails>({
					headerName: '',
					colId: 'radioBtn',
					minWidth: 50,
					maxWidth: 50,
					width: 50,
					onCellClicked: event => {
						this.onOrgSelection(event, false);
						event.api.refreshCells({ columns: [event.column] });
					},
					cellRendererParams: {
						disabled: false,
						isChecked: (params: ICellRendererParams<unknown, unknown>) =>
							Boolean((Number(params.node.id) === this.nodeSelectedMatchingOrg) && !this.isNewSubOrg)
					}
				}),
				new CommonColDef<OrganizationDetails>({
					headerName: this.translationService.getTR('add-suborg.id'),
					field: 'id',
				}),
				new CommonColDef<OrganizationDetails>({
					headerName: this.translationService.getTR('add-suborg.org-name'),
					field: 'name',
					width: 30
				}),
				new CommonColDef<OrganizationDetails>({
					headerName: this.translationService.getTR('add-suborg.vat-number'),
					field: 'vatNumber',
				}),
			]
		});
		this.newSubOrgGridOptions = new CommonGridOptions<OrganizationDetails>({
			translationService: this.translationService,
			multiSortKey: 'ctrl',
			domLayout: 'autoHeight',
			columnDefs: [
				new RadioBtnColDef<OrganizationDetails>({
					headerName: '',
					colId: 'radioBtn',
					minWidth: 50,
					maxWidth: 50,
					width: 50,
					pinned: 'left',
					onCellClicked: event => {
						this.onOrgSelection(event, true);
						event.api.refreshCells({ columns: [event.column] });
					},
					cellRendererParams: {
						disabled: false,
						isChecked: (params: ICellRendererParams<unknown, unknown>) =>
							Boolean((Number(params.node.id) === this.nodeSelectedMatchingOrg) && this.isNewSubOrg)
					}
				}),
				new CommonColDef<OrganizationDetails>({
					headerName: this.translationService.getTR('add-suborg.org-name'),
					field: 'name',
					width: 30
				}),
				new CommonColDef<OrganizationDetails>({
					headerName: this.translationService.getTR('add-suborg.vat-number'),
					field: 'vatNumber',
				}),
			]
		});
		this.sub = this.addSubOrgService.organizationName.subscribe(organizationName => {
			this.organizationName = organizationName;
		});
		this.sub = this.addSubOrgService.vatNumber.subscribe(vatNumber => {
			this.vatNumber = vatNumber;
		});
		this.sub = this.addSubOrgService.orgId.subscribe(orgId => {
			this.organizationId = orgId;
		});
		this.sub = this.addSubOrgService.isAnyOneOrgSelected.subscribe(isAnyOneOrgSelected => {
			this.isAnyOneSelected = isAnyOneOrgSelected;
		});
		this.sub = this.addSubOrgService.nodeSelectedMatchingOrgs.subscribe(nodeSelected => {
			this.nodeSelectedMatchingOrg = nodeSelected;
		});

		this.sub = this.addSubOrgService.isNewSubOrg.subscribe(isNewSubOrg => {
			this.isNewSubOrg = isNewSubOrg;
		});
	}

	ngOnInit() {
		(async () => {
			await this.findMatchingOrganizations();
			await this.addSubOrgService.vatNumberExists(this.vatNumber).then(
				value => {
					this.addSubOrgService.updateVatNumberExists(value);
				}
			);
		})().catch((err: unknown) => {
			console.error(err);
		});
	}

	onGridReady(params: GridReadyEvent) {
		this.gridApi = params.api;
		params.api.sizeColumnsToFit();
		const resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
		if (!this.resizeSub) {
			this.resizeSub = resizeSub;
		} else {
			this.resizeSub.add(resizeSub);
		}
	}

	onNewGridReady(params: GridReadyEvent) {
		this.gridNewApi = params.api;
		params.api.sizeColumnsToFit();
		const resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
		if (!this.resizeSub) {
			this.resizeSub = resizeSub;
		} else {
			this.resizeSub.add(resizeSub);
		}
	}

	async findMatchingOrganizations() {
		await this.addSubOrgService.getVisibleSubOrganizations(
			this.vatNumber,
			this.organizationName
		).then(
			organizationData => {
				this.newSubOrg = {
					name: this.organizationName,
					vatNumber: this.vatNumber
				};
				this.newSubOrgRowData.push(this.newSubOrg);
				for (const data of organizationData) {
					if (data.id !== Number(this.organizationId)) {
						const orgDetails: OrganizationDetails = {
							id: data.id,
							name: data.name,
							vatNumber: data.vatNumber
						};
						this.matchingOrgs.push(orgDetails);
					}
				}
				this.isLoading = false;
			}
		);
	}

	createFilter() {
		const search = term.escape(this.organizationName);
		const nameField: FieldExpression = {
			field: 'name',
			term: search,
			quoted: false
		};
		const vatNumberField: FieldExpression = {
			field: 'vatNumber',
			term: this.vatNumber,
			quoted: false
		};

		const finalNode: NodeExpression = {
			left: nameField,
			operator: 'AND',
			parenthesized: true,
			right: vatNumberField
		};
		return luceneToString(finalNode);
	}

	onOrgSelection(event: CellClickedEvent<OrganizationDetails>, isNewSubOrg: boolean) {
		this.addSubOrgService.updateIsAnyOneOrgChoiceSelected(true);
		this.addSubOrgService.updateNodeSelectedMatchingOrgs(Number(event.node.id));
		this.addSubOrgService.updateIsNewSubOrg(isNewSubOrg);
		this.selectedRow = event.node.data;
		if (!isNewSubOrg) {
			this.addSubOrgService.setSelectedOrg(this.selectedRow);
		}
	}

	checkErrors(): boolean | undefined {
		this.nextClicked = true;
		this.sub = this.addSubOrgService.isAnyOneOrgSelected.subscribe(isAnyOneOrgSelected => {
			this.isAnyOneSelected = isAnyOneOrgSelected;
		});
		this.sub = this.addSubOrgService.vatNumberExistsCheck.subscribe(vatNumberExists => {
			this.vatNumberExists = vatNumberExists;
		});
		return !this.isNewSubOrg ? this.isAnyOneSelected : this.isAnyOneSelected && !this.vatNumberExists;
	}

	ngOnDestroy() {
		if (this.sub !== undefined) {
			this.sub.unsubscribe();
		}
	}
}
